import React, { useCallback, useEffect, useState } from 'react';
import { KeyRound, Package, Plus } from 'lucide-react';
import { toast } from 'sonner';

import { apiService } from '../services/api';
import type { TemplateProcessor, TemplateProcessorVersion } from '../types/api';
import { humanizeError, templatePrivacyBlockers } from '../lib/errors';
import { EMPTY_VALUE, formatDate, formatDateTime, formatNumber } from '../lib/formatters';
import { cn, EYEBROW } from '../lib/utils';
import { statusLabel } from '../lib/statusRegistry';
import { DialogSectionRail, type DialogSection } from './DialogSectionRail';
import { DisclosureChevron } from './DisclosureChevron';
import { RecordDialogHeader } from './RecordDialogHeader';
import { CredentialField } from './CredentialField';
import { EmptyState } from './EmptyState';
import { Field } from './Field';
import { RichTextView } from './RichText';
import { SaveBar } from './SaveBar';
import { StatusBadge } from './StatusBadge';
import { STATUS_TONE_FILL, statusTone } from '../lib/statusRegistry';
import { VisibilityField } from './VisibilityField';
import { trimmedText, useRecordChanges, type RecordComparators } from '../hooks/useRecordChanges';
import { useDiscardGuard } from '../hooks/useDiscardGuard';
import { Alert, AlertDescription } from './ui/alert';
import { Button } from './ui/button';
import { PropertyList, PropertyRow, SectionHeading } from './PropertyList';
import { EnvironmentVariablesList } from './EnvironmentVariablesList';
import { Separator } from './ui/separator';
import { Dialog, DialogContent } from './ui/dialog';
import { Input } from './ui/input';
import { Skeleton } from './ui/skeleton';
import { Textarea } from './ui/textarea';
import { EventTypeChips } from './EventTypeChips';
import { formatSensorTypes } from '../lib/labels';

/** Uppercase eyebrow, the one label treatment this dialog uses. */

/** Picks the best display label for a version: semver, else tag, else dash. */
const displayVersion = (
  version: Pick<TemplateProcessorVersion, 'version' | 'tag'>,
): string => version.version || version.tag || EMPTY_VALUE;

/** A digest is 71 characters of noise; the head is what a human compares. */
const shortDigest = (digest: string): string =>
  digest.length > 20 ? `${digest.slice(0, 14)}…${digest.slice(-4)}` : digest;

type Section = 'info' | 'versions';

/** The three fields `PUT /template-processors/{slug}` will accept as a form. */
interface EditableFields {
  name: string;
  description: string;
  isPublic: boolean;
}

/** Name and description differ only by more than surrounding whitespace. */
const WORKBENCH_COMPARATORS: RecordComparators<EditableFields> = {
  name: trimmedText,
  description: trimmedText,
};

const editableOf = (template: TemplateProcessor): EditableFields => ({
  name: template.name,
  description: template.description || '',
  isPublic: template.isPublic,
});

export interface TemplateWorkbenchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The row that was clicked. Null between opens. */
  template: TemplateProcessor | null;
  organizationId: string;
  accessToken: string;
  mayWriteTemplates: boolean;
  /**
   * `processor_template_versions:read`, which gates the history endpoint.
   *
   * Without it there is no Versions section at all — not an empty one, and not
   * one that errors. A member who cannot read the history is not helped by a
   * rail entry that leads to a 403.
   */
  mayReadVersions: boolean;
  mayWriteVersions: boolean;
  mayReadCredentials: boolean;
  /** Folds a server-confirmed template back into the page's array. */
  onTemplateChanged: (updated: TemplateProcessor) => void;
  /** Opens the publish wizard ABOVE this dialog, which stays mounted. */
  onNewVersion: (template: TemplateProcessor) => void;
  /**
   * Bumped by the page when a write elsewhere — the wizard registering a
   * version — may have changed this template's history. The dialog cannot see
   * that write, so the page tells it to ask again.
   */
  versionsToken: number;
}

/**
 * One template, read and edited on one surface.
 *
 * This replaces four dialogs (details, edit, credentials, versions) and the
 * three row icons that opened three of them. The cost of that arrangement was
 * never the clicks: it was that no single surface answered "what is this
 * template and is it healthy", so the reader assembled the object in their head
 * across four views sharing no layout and no vocabulary.
 *
 * The rule the layout is built on: an INPUT means you can change it, a VALUE
 * means you cannot. Nothing is styled as a field that the API will refuse, and
 * nothing changeable hides behind a second dialog — which is what lets the
 * surface teach itself without a word of instruction.
 *
 * The publish wizard is the one thing that stays separate, because it is a task
 * with a beginning and an end. It layers above this dialog rather than
 * replacing it: when the wizard finishes, the destination is still here, with
 * one more version in the list.
 */
export const TemplateWorkbenchDialog: React.FC<TemplateWorkbenchDialogProps> = ({
  open,
  onOpenChange,
  template,
  organizationId,
  accessToken,
  mayWriteTemplates,
  mayReadVersions,
  mayWriteVersions,
  mayReadCredentials,
  onTemplateChanged,
  onNewVersion,
  versionsToken,
}) => {
  const [section, setSection] = useState<Section>('info');

  const [form, setForm] = useState<EditableFields>({ name: '', description: '', isPublic: false });
  /**
   * What the server last confirmed — the thing "changed" is measured against.
   *
   * State rather than a memo over `template`, and that is load-bearing. The
   * page replaces the template object whenever anything writes (Make live
   * does, from the other section of this very dialog), and a memo would rebuild
   * the baseline from the new object mid-edit: the Save button would go quiet
   * and the reader's typing would look saved when it was not.
   */
  const [baseline, setBaseline] = useState<EditableFields>({ name: '', description: '', isPublic: false });
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  /** The one refusal that belongs to a field rather than to the form. */
  const [visibilityError, setVisibilityError] = useState<string | null>(null);

  const [credentials, setCredentials] = useState<{ clientId: string; clientSecret: string } | null>(null);
  const [isCredentialsLoading, setIsCredentialsLoading] = useState(false);
  const [credentialsError, setCredentialsError] = useState<string | null>(null);
  /** True right after Create credential: focus moves to the credential it rendered. */
  const [credentialsJustCreated, setCredentialsJustCreated] = useState(false);

  const [versions, setVersions] = useState<TemplateProcessorVersion[] | null>(null);
  const [isVersionsLoading, setIsVersionsLoading] = useState(false);
  const [versionsError, setVersionsError] = useState<string | null>(null);
  const [expandedVersionId, setExpandedVersionId] = useState<string | null>(null);
  const [settingCurrentVersionId, setSettingCurrentVersionId] = useState<string | null>(null);

  const templateId = template?.id ?? null;
  const templateSlug = template?.slug ?? null;

  /**
   * Resets the whole dialog when it opens on a template.
   *
   * Keyed on the template's ID, deliberately not on the object: the page hands
   * back a NEW object every time anything writes, and re-running this on that
   * would throw away whatever the reader had typed. A different row, or a
   * reopen, is the only thing that should start over.
   */
  useEffect(() => {
    if (!open || !template) return;
    const fields = editableOf(template);
    setSection('info');
    setForm(fields);
    setBaseline(fields);
    setIsSaving(false);
    setSaveError(null);
    setVisibilityError(null);
    setCredentials(null);
    setCredentialsError(null);
    setCredentialsJustCreated(false);
    setVersions(null);
    setVersionsError(null);
    setExpandedVersionId(null);
    setSettingCurrentVersionId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, templateId]);

  /**
   * Reads the publisher credential, on its own, without holding up the dialog.
   *
   * Only ever a GET here. A template created before credentials came with the
   * template has none, and minting one is a WRITE — offered as a button below,
   * never done because someone opened a dialog.
   */
  useEffect(() => {
    if (!open || !template || !mayReadCredentials) return;
    if (!template.hasCredentials || !template.publisherAppClientId) return;

    let cancelled = false;
    const appClientId = template.publisherAppClientId;
    setIsCredentialsLoading(true);
    setCredentialsError(null);
    apiService
      .getAppCredentials(appClientId, accessToken)
      .then((result) => {
        if (cancelled) return;
        setCredentials({ clientId: result.appClientId, clientSecret: result.clientSecret });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error('Failed to load template credentials:', err);
        setCredentialsError(humanizeError(err, "Couldn't load the publisher credential"));
      })
      .finally(() => {
        if (!cancelled) setIsCredentialsLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, templateId, accessToken, mayReadCredentials]);

  /**
   * Reads the version history.
   *
   * On open rather than on entering the Versions section, because the count is
   * in the rail — it is what turns two navigation entries into a status
   * summary, and it has to be right before anyone clicks. `versionsToken`
   * re-runs this after the wizard registers a version.
   */
  useEffect(() => {
    if (!open || !templateSlug || !mayReadVersions) return;

    let cancelled = false;
    setIsVersionsLoading(true);
    setVersionsError(null);
    apiService
      .getTemplateProcessorVersions(templateSlug, organizationId, accessToken)
      .then((data) => {
        if (!cancelled) setVersions(data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error('Failed to load template versions:', err);
        setVersionsError(humanizeError(err, "Couldn't load the version history"));
      })
      .finally(() => {
        if (!cancelled) setIsVersionsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, templateSlug, organizationId, accessToken, versionsToken, mayReadVersions]);

  /**
   * Exactly what differs from the last server-confirmed values.
   *
   * Doubles as the request body — the update route treats an absent field as
   * untouched, so sending the whole form would let this dialog quietly undo
   * somebody else's concurrent edit to a field nobody here changed.
   */
  const { changes, changeCount, isDirty } = useRecordChanges(baseline, form, WORKBENCH_COMPARATORS);
  const nameError = changes.name !== undefined && !changes.name ? 'Name is required.' : null;

  // A save failure describes unsaved changes; once there are none, it is stale.
  useEffect(() => {
    if (!isDirty) setSaveError(null);
  }, [isDirty]);

  const discardChanges = useCallback(() => {
    setForm(baseline);
    setSaveError(null);
    setVisibilityError(null);
  }, [baseline]);

  /**
   * X, Escape, outside click: one guard covers every way out, so a dialog you
   * can dismiss by clicking beside it never silently drops what someone typed.
   */
  const guard = useDiscardGuard({
    isDirty,
    isBusy: isSaving,
    onClose: () => onOpenChange(false),
    onDiscard: discardChanges,
    recordNoun: template ? `template "${template.name}"` : 'this template',
  });

  const handleSave = async () => {
    if (!template || !isDirty || nameError || isSaving) return;

    try {
      setIsSaving(true);
      setSaveError(null);
      setVisibilityError(null);
      const updated = await apiService.updateTemplateProcessor(
        template.slug,
        changes,
        organizationId,
        accessToken,
      );
      const fields = editableOf(updated);
      setForm(fields);
      setBaseline(fields);
      onTemplateChanged(updated);
      toast.success('Template updated');
    } catch (err: unknown) {
      console.error('Failed to update template:', err);
      // Going private while a public or foreign lens still holds the template
      // is refused BY DATA, and the refusal names the lenses standing in the
      // way. That answer belongs under the switch that caused it, naming them —
      // a toast would take the one actionable part of the message away again.
      const blockers = templatePrivacyBlockers(err);
      if (blockers) {
        setVisibilityError(
          `Still used by ${blockers.join(', ')}. Remove it from those lenses before making it private.`,
        );
      } else {
        setSaveError(humanizeError(err, "Couldn't save the template"));
      }
    } finally {
      setIsSaving(false);
    }
  };

  /** The one-time heal for a template that predates auto-provisioned credentials. */
  const handleGenerateCredentials = async () => {
    if (!template) return;
    try {
      setIsCredentialsLoading(true);
      setCredentialsError(null);
      const result = await apiService.createTemplateCredentials(
        template.slug,
        organizationId,
        accessToken,
      );
      setCredentials(result);
      // No toast: the credential rendered in place is the acknowledgement,
      // and focus moves to it as the button that made it unmounts.
      setCredentialsJustCreated(true);
      onTemplateChanged({ ...template, hasCredentials: true, publisherAppClientId: result.clientId });
    } catch (err: unknown) {
      console.error('Failed to generate template credentials:', err);
      setCredentialsError(humanizeError(err, "Couldn't create a publisher credential"));
    } finally {
      setIsCredentialsLoading(false);
    }
  };

  const handleSetCurrent = async (versionId: string) => {
    if (!template) return;
    try {
      setVersionsError(null);
      setSettingCurrentVersionId(versionId);
      const updated = await apiService.updateTemplateProcessor(
        template.slug,
        { processorTemplateVersionId: versionId },
        organizationId,
        accessToken,
      );
      // The template object changes underneath a form that may be dirty. Only
      // `onTemplateChanged` runs — the baseline is untouched, because none of
      // the three editable fields moved, and the reader's typing survives.
      onTemplateChanged(updated);
      const madeLive = versions?.find((version) => version.id === versionId);
      setVersions((prev) =>
        prev ? prev.map((version) => ({ ...version, isActive: version.id === versionId })) : prev,
      );
      toast.success(madeLive ? `${displayVersion(madeLive)} is now live` : 'Version is now live');
    } catch (err: unknown) {
      console.error('Failed to make the version live:', err);
      setVersionsError(humanizeError(err, "Couldn't make the version live"));
    } finally {
      setSettingCurrentVersionId(null);
    }
  };

  if (!template) return null;

  const current = template.selectedVersion;
  const latest = template.latestVersion;
  const pendingReview = latest?.reviewStatus === 'pending';
  const versionCount = versions?.length ?? null;

  /*
   * Versions disappears with the permission that reaches it, and the rail
   * refuses to render below two entries — so a member who may not list a
   * history gets a plain dialog rather than a rail leading nowhere.
   */
  const sections: Array<DialogSection<Section>> = [
    { value: 'info', label: 'Info' },
    ...(mayReadVersions
      ? [{
        value: 'versions' as const,
        label: 'Versions',
        count: versionCount,
        attention: pendingReview,
        note: pendingReview ? `1 ${statusLabel('pending_review').toLowerCase()}` : undefined,
      }]
      : []),
  ];

  return (
    <>
      <Dialog open={open} onOpenChange={guard.onOpenChange} pending={isSaving}>
        <DialogContent size="2xl" frame className="h-[660px]">
          {/* Identity. Fixed, so it stays put when the section changes. */}
          <RecordDialogHeader
            title={template.name}
            id={template.slug}
            srDescription="Template slug"
            badges={
              <>
                {/* "Live" only when the selected version is active: revoking
                    clears isActive but leaves the pointer, and that version is
                    Revoked, not live. */}
                {current?.isActive ? (
                  <StatusBadge status="live" label={`Live ${displayVersion(current)}`} />
                ) : current ? (
                  <StatusBadge
                    status={current.reviewStatus === 'pending' ? 'pending_review' : current.reviewStatus}
                  />
                ) : (
                  <StatusBadge status="draft" />
                )}
                <StatusBadge status={template.isPublic ? 'public' : 'private'} />
              </>
            }
          />

          <div
            className={cn(
              'grid min-h-0 flex-1 grid-cols-1 overflow-hidden',
              sections.length > 1 && 'sm:grid-cols-[13rem_1fr]',
            )}
          >
            <DialogSectionRail
              label="Template sections"
              sections={sections}
              value={section}
              onChange={setSection}
            />

            <div className="flex min-h-0 flex-col">
              <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
                {section === 'info' ? (
                  <InfoSection
                    template={template}
                    form={form}
                    setForm={setForm}
                    mayWriteTemplates={mayWriteTemplates}
                    mayReadVersions={mayReadVersions}
                    mayWriteVersions={mayWriteVersions}
                    mayReadCredentials={mayReadCredentials}
                    nameError={nameError}
                    visibilityError={visibilityError}
                    focusNewCredential={credentialsJustCreated}
                    credentials={credentials}
                    isCredentialsLoading={isCredentialsLoading}
                    credentialsError={credentialsError}
                    onGenerateCredentials={handleGenerateCredentials}
                    onShowVersions={() => setSection('versions')}
                  />
                ) : (
                  <VersionsSection
                    template={template}
                    versions={versions}
                    isLoading={isVersionsLoading}
                    error={versionsError}
                    expandedVersionId={expandedVersionId}
                    onToggleExpanded={(id) =>
                      setExpandedVersionId((prev) => (prev === id ? null : id))
                    }
                    mayWriteVersions={mayWriteVersions}
                    settingCurrentVersionId={settingCurrentVersionId}
                    onSetCurrent={handleSetCurrent}
                    onNewVersion={() => onNewVersion(template)}
                  />
                )}
              </div>

              {/*
                The save bar exists only while something is unsaved, and then
                it follows the reader to Versions — exactly when they need to be
                told. A member who may not write templates never sees it: a form
                you cannot submit is a worse thing to hand someone than a record.
                The failure lives in the bar, so a save pressed from Versions
                shows it too.
              */}
              {mayWriteTemplates ? (
                <SaveBar
                  changeCount={changeCount}
                  onDiscard={discardChanges}
                  onSave={() => {
                    void handleSave();
                  }}
                  isSaving={isSaving}
                  saveDisabled={Boolean(nameError)}
                  error={saveError}
                />
              ) : null}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {guard.guardDialog}
    </>
  );
};

/* ------------------------------------------------------------------ */
/* Info                                                                */
/* ------------------------------------------------------------------ */

interface InfoSectionProps {
  template: TemplateProcessor;
  form: EditableFields;
  setForm: React.Dispatch<React.SetStateAction<EditableFields>>;
  mayWriteTemplates: boolean;
  mayReadVersions: boolean;
  mayWriteVersions: boolean;
  mayReadCredentials: boolean;
  nameError: string | null;
  visibilityError: string | null;
  /** Focus the Client ID copy button when the credential has just been created. */
  focusNewCredential: boolean;
  credentials: { clientId: string; clientSecret: string } | null;
  isCredentialsLoading: boolean;
  credentialsError: string | null;
  onGenerateCredentials: () => void;
  onShowVersions: () => void;
}

/**
 * Details first, facts after.
 *
 * The three editable fields open the section on purpose: a dialog that opens on
 * read-only values teaches the reader it is a viewer, and they stop looking for
 * the edit. Registry and credentials close it because they are reference
 * material — wanted when you are setting up a push, never when you are reading.
 */
const InfoSection: React.FC<InfoSectionProps> = ({
  template,
  form,
  setForm,
  mayWriteTemplates,
  mayReadVersions,
  mayWriteVersions,
  mayReadCredentials,
  nameError,
  visibilityError,
  focusNewCredential,
  credentials,
  isCredentialsLoading,
  credentialsError,
  onGenerateCredentials,
  onShowVersions,
}) => {
  const current = template.selectedVersion;
  const latest = template.latestVersion;
  const feedbackVersion = latest?.reviewNotes && latest.reviewStatus !== 'pending' ? latest : null;

  return (
    <div className="space-y-6">
      <section className="space-y-4">
        <SectionHeading>Details</SectionHeading>

        {mayWriteTemplates ? (
          <>
            <Field label="Name" required error={nameError}>
              {(control) => (
                <Input
                  {...control}
                  value={form.name}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, name: event.target.value }))
                  }
                />
              )}
            </Field>
            <Field label="Description">
              {(control) => (
                <Textarea
                  {...control}
                  rows={2}
                  value={form.description}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, description: event.target.value }))
                  }
                />
              )}
            </Field>
            <VisibilityField
              kind="template"
              checked={form.isPublic}
              onCheckedChange={(checked) => setForm((prev) => ({ ...prev, isPublic: checked }))}
              error={visibilityError}
            />
          </>
        ) : (
          <>
            <PropertyList>
              <PropertyRow label="Name">{template.name}</PropertyRow>
              <PropertyRow label="Description">{template.description}</PropertyRow>
            </PropertyList>
            {/* Readers get the same field, read-only: the value and what it means. */}
            <VisibilityField kind="template" checked={template.isPublic} readOnly />
          </>
        )}
      </section>

      <Separator />

      <section className="space-y-2">
        <SectionHeading>Current version</SectionHeading>
        {current ? (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <p className="font-mono text-sm font-medium">{displayVersion(current)}</p>
            <p className="text-xs text-muted-foreground">
              Pushed {formatDateTime(current.createdAt)}
            </p>
            {/*
              Only when it is NOT approved. A version can be live only if a
              reviewer approved it — the database trigger refuses any other
              pairing — so an "Approved" badge beside the live version says
              something that is true by construction, which is decoration. A
              badge here means something went wrong.
            */}
            {current.reviewStatus !== 'approved' ? (
              <StatusBadge
                status={current.reviewStatus === 'pending' ? 'pending_review' : current.reviewStatus}
              />
            ) : null}
            {mayReadVersions ? (
              <Button
                type="button"
                variant="link"
                className="ml-auto h-auto p-0 text-sm"
                onClick={onShowVersions}
              >
                All versions
              </Button>
            ) : null}
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-sm text-muted-foreground">
              {latest
                ? 'A version has been pushed, but none is live yet.'
                : 'Nothing has been pushed to this template yet.'}
            </p>
            {mayReadVersions ? (
              <Button
                type="button"
                variant="link"
                className="h-auto p-0 text-sm"
                onClick={onShowVersions}
              >
                All versions
              </Button>
            ) : null}
          </div>
        )}

        {latest?.reviewStatus === 'pending' ? (
          <div className="rounded-md border border-info-border bg-info-subtle px-3 py-2">
            <p className="text-sm font-medium">
              {displayVersion(latest)} is waiting for review
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Submitted {formatDateTime(latest.submittedAt ?? latest.createdAt)}. It will not run
              against any sensors until an administrator approves it.
            </p>
          </div>
        ) : null}

        {/*
          The reviewer's verdict, written by an administrator in ANOTHER
          organization and the only place this publisher can read it. Rendered
          through RichTextView, which purifies before it reaches the DOM.
        */}
        {feedbackVersion ? (
          <div className="rounded-md border-l-2 border-l-muted-foreground/40 bg-muted/40 px-3 py-2">
            <p className={cn('mb-1', EYEBROW)}>
              Reviewer feedback
              {feedbackVersion.reviewedAt ? ` · ${formatDateTime(feedbackVersion.reviewedAt)}` : ''}
            </p>
            <RichTextView html={feedbackVersion.reviewNotes ?? ''} />
          </div>
        ) : null}
      </section>

      <Separator />

      {/*
        No section eyebrow here, unlike its siblings: this block holds ONE
        field and CredentialField labels its own well, so a "Registry" heading
        above "REPOSITORY URI" would be two uppercase labels in a row saying
        the same thing. The rule underneath is that structure is information —
        a heading earns its place by grouping something.
      */}
      <section>
        {template.ecr.repositoryUri ? (
          <CredentialField label="Repository URI" value={template.ecr.repositoryUri} />
        ) : (
          <>
            <p className={cn('mb-1.5', EYEBROW)}>Repository URI</p>
            <p className="text-sm text-muted-foreground">{EMPTY_VALUE}</p>
          </>
        )}
      </section>

      {/*
        Absent, not masked, for a member without the permission. An empty
        labelled field invites a support ticket; nothing at all says nothing.
      */}
      {mayReadCredentials || (!template.hasCredentials && mayWriteVersions) ? (
        <>
          <Separator />
          <section className="space-y-3">
            <SectionHeading>Publisher credential</SectionHeading>

            {credentialsError ? (
              <Alert variant="destructive">
                <AlertDescription>{credentialsError}</AlertDescription>
              </Alert>
            ) : null}

            {isCredentialsLoading ? (
              <div className="grid gap-4 sm:grid-cols-2" aria-hidden="true">
                <Skeleton className="h-14" />
                <Skeleton className="h-14" />
              </div>
            ) : credentials ? (
              <div className="grid gap-4 sm:grid-cols-2">
                <CredentialField label="Client ID" value={credentials.clientId} autoFocusCopy={focusNewCredential} />
                {/*
                  Masked, copy-only, and that is the whole product's convention:
                  a template secret can be copied as often as you like, so there
                  is never a reason to put it on a screen a shoulder can read.
                */}
                <CredentialField
                  label="Client secret"
                  value={credentials.clientSecret}
                  secret
                  revealable={false}
                />
              </div>
            ) : !template.hasCredentials ? (
              <div className="flex flex-wrap items-center gap-3">
                <p className="text-sm text-muted-foreground">
                  This template predates automatic credentials and has none.
                </p>
                {mayWriteVersions ? (
                  <Button type="button" variant="outline" size="sm" onClick={onGenerateCredentials}>
                    <KeyRound aria-hidden="true" />
                    Create credential
                  </Button>
                ) : null}
              </div>
            ) : null}
          </section>
        </>
      ) : null}

      <p className="pt-1 text-xs text-muted-foreground">
        Created {formatDate(template.createdAt)} · Updated {formatDate(template.updatedAt)}
      </p>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Versions                                                            */
/* ------------------------------------------------------------------ */

interface VersionsSectionProps {
  template: TemplateProcessor;
  versions: TemplateProcessorVersion[] | null;
  isLoading: boolean;
  error: string | null;
  expandedVersionId: string | null;
  onToggleExpanded: (id: string) => void;
  mayWriteVersions: boolean;
  settingCurrentVersionId: string | null;
  onSetCurrent: (versionId: string) => void;
  onNewVersion: () => void;
}

/**
 * Rows, not cards.
 *
 * Cards give every version equal weight and a border each; a version list is
 * scanned, and scanning wants one alignment and a quiet ground. State rides on
 * a left stripe — green for what is live, amber for what is waiting, red for
 * what was refused, nothing at all for the merely historical — so four fit
 * without scrolling and forty still read.
 */
const VersionsSection: React.FC<VersionsSectionProps> = ({
  template,
  versions,
  isLoading,
  error,
  expandedVersionId,
  onToggleExpanded,
  mayWriteVersions,
  settingCurrentVersionId,
  onSetCurrent,
  onNewVersion,
}) => (
  <div className="space-y-4">
    <div className="flex items-center justify-between gap-3">
      <p className={EYEBROW}>
        {versions ? `${versions.length} ${versions.length === 1 ? 'version' : 'versions'}` : 'Versions'}
      </p>
      {mayWriteVersions ? (
        <Button type="button" size="sm" onClick={onNewVersion}>
          <Plus aria-hidden="true" />
          New version
        </Button>
      ) : null}
    </div>

    {error ? (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    ) : null}

    {isLoading && !versions ? (
      <div className="space-y-2" aria-hidden="true">
        <Skeleton className="h-14" />
        <Skeleton className="h-14" />
      </div>
    ) : null}

    {versions && versions.length === 0 ? (
      <EmptyState
        icon={Package}
        title="No versions yet"
        description="Push an image to this template's registry with its publisher credential, and it appears here for review."
      />
    ) : null}

    {versions && versions.length > 0 ? (
      <ul className="space-y-2">
        {versions.map((version) => (
          <VersionRow
            key={version.id}
            version={version}
            isCurrent={template.processorTemplateVersionId === version.id}
            isExpanded={expandedVersionId === version.id}
            onToggle={() => onToggleExpanded(version.id)}
            mayWriteVersions={mayWriteVersions}
            isSettingCurrent={settingCurrentVersionId === version.id}
            isAnySettingCurrent={settingCurrentVersionId !== null}
            onSetCurrent={() => onSetCurrent(version.id)}
          />
        ))}
      </ul>
    ) : null}
  </div>
);

interface VersionRowProps {
  version: TemplateProcessorVersion;
  isCurrent: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  mayWriteVersions: boolean;
  isSettingCurrent: boolean;
  isAnySettingCurrent: boolean;
  onSetCurrent: () => void;
}

const VersionRow: React.FC<VersionRowProps> = ({
  version,
  isCurrent,
  isExpanded,
  onToggle,
  mayWriteVersions,
  isSettingCurrent,
  isAnySettingCurrent,
  onSetCurrent,
}) => {
  const label = displayVersion(version);
  const detailsId = `version-details-${version.id}`;
  /** Live = the selected version AND active. A revoked selected version is not. */
  const isLive = isCurrent && version.isActive;
  /*
   * Only an APPROVED version can be made live: the API 409s anything else
   * and the database trigger refuses the write underneath it. So the button is
   * ABSENT where it cannot succeed, rather than present and disabled — a
   * control whose only possible outcome is an error message is worse than no
   * control.
   */
  const canSetCurrent = mayWriteVersions && !isLive && version.reviewStatus === 'approved';

  /*
   * The stripe takes the tone of the badge on the same row, from the registry
   * (statusTone) — never hand-picked: pending is blue like "In review", rejected
   * red (it never ran), revoked amber (it ran and was withdrawn). An approved
   * version that is not live, and anything muted, gets no stripe.
   */
  const reviewKey = version.reviewStatus === 'pending' ? 'pending_review' : version.reviewStatus;
  const stripeKey = isLive ? 'live' : reviewKey;
  const stripeTone = statusTone(stripeKey);
  const stripe = stripeKey === 'approved' || stripeTone === 'muted' ? null : STATUS_TONE_FILL[stripeTone];

  return (
    <li className="relative overflow-hidden rounded-md border bg-card">
      {stripe ? (
        <span aria-hidden="true" className={cn('absolute inset-y-0 left-0 w-0.5', stripe)} />
      ) : null}

      <div className="flex items-start gap-3 py-3 pl-4 pr-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-mono text-sm font-medium">{label}</p>
            {isLive ? <StatusBadge status="live" /> : null}
            {/* Same rule as Info: "Live, Approved" is one fact told twice. And
                Live and Revoked can never appear together: isLive needs isActive. */}
            {isLive && version.reviewStatus === 'approved' ? null : (
              <StatusBadge status={reviewKey} />
            )}
          </div>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">
            Pushed {formatDateTime(version.createdAt)} · {shortDigest(version.digest)}
          </p>
        </div>

        {canSetCurrent ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onSetCurrent}
            disabled={isAnySettingCurrent}
            loading={isSettingCurrent}
          >
            Make live
          </Button>
        ) : null}

        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="shrink-0"
          aria-expanded={isExpanded}
          aria-controls={detailsId}
          aria-label={isExpanded ? `Hide details for ${label}` : `Show details for ${label}`}
          onClick={onToggle}
        >
          <DisclosureChevron open={isExpanded} />
        </Button>
      </div>

      {/* Collapsed by default: none of this is what you came to check. */}
      {isExpanded ? (
        <div id={detailsId} className="space-y-3 border-t px-4 py-3 text-sm">
          <div>
            <p className={EYEBROW}>Release notes</p>
            <p className="mt-1 whitespace-pre-line break-words">
              {version.releaseNotes || EMPTY_VALUE}
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <p className={EYEBROW}>Sensor types</p>
              <p className="mt-1 break-words">{formatSensorTypes(version.supportedSensorTypes)}</p>
            </div>
            <div>
              <p className={EYEBROW}>Event types</p>
              {/* By name from the API. An API without `eventTypes` gets a count, never the ids. */}
              {version.eventTypes ? (
                <EventTypeChips
                  eventTypes={version.eventTypes}
                  className="mt-1"
                  empty={<p className="mt-1">{EMPTY_VALUE}</p>}
                />
              ) : (
                <p className="mt-1">
                  {version.supportedTags.length > 0
                    ? `${formatNumber(version.supportedTags.length)} ${version.supportedTags.length === 1 ? 'event type' : 'event types'}`
                    : EMPTY_VALUE}
                </p>
              )}
            </div>
          </div>
          {/* Absent against an API without the field: nothing, never the raw runtime config. */}
          {version.environment ? (
            <div>
              <p className={EYEBROW}>Environment variables</p>
              <EnvironmentVariablesList
                variables={version.environment}
                className="mt-1"
                empty={<p className="mt-1">None</p>}
              />
            </div>
          ) : null}
          <div>
            <p className={EYEBROW}>Image</p>
            <p className="mt-1 break-all font-mono text-xs text-muted-foreground">
              {version.imageUri}
            </p>
          </div>
          <div>
            <p className={EYEBROW}>Digest</p>
            <p className="mt-1 break-all font-mono text-xs text-muted-foreground">
              {version.digest}
            </p>
          </div>
          {version.reviewNotes ? (
            <div>
              <p className={EYEBROW}>
                Reviewer feedback
                {version.reviewedAt ? ` · ${formatDateTime(version.reviewedAt)}` : ''}
              </p>
              <div className="mt-1 rounded-md border-l-2 border-l-muted-foreground/40 bg-muted/40 px-3 py-2">
                <RichTextView html={version.reviewNotes} />
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </li>
  );
};

export default TemplateWorkbenchDialog;
